import { html, css } from 'lit'
import { customElement, state, property } from 'lit/decorators.js'
import { PageView } from '@operato/shell'
import { client } from '@operato/graphql'
import gql from 'graphql-tag'

/**
 * Workflow Builder Page
 *
 * Generic workflow builder UI for creating and editing labeling workflows
 */
@customElement('labeling-workflow-builder-page')
export class LabelingWorkflowBuilderPage extends PageView {
  static styles = [
    css`
      :host {
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: var(--md-sys-color-surface);
      }

      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 20px;
        background-color: var(--md-sys-color-surface-variant);
        border-bottom: 1px solid var(--md-sys-color-outline);
      }

      .header h1 {
        margin: 0;
        font-size: 24px;
        color: var(--md-sys-color-on-surface);
      }

      .header-actions {
        display: flex;
        gap: 10px;
      }

      .content {
        display: flex;
        flex: 1;
        overflow: hidden;
      }

      .sidebar {
        width: 300px;
        border-right: 1px solid var(--md-sys-color-outline);
        overflow-y: auto;
        padding: 20px;
      }

      .main-area {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
      }

      .form-group {
        margin-bottom: 20px;
      }

      .form-group label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: var(--md-sys-color-on-surface);
      }

      .form-group input,
      .form-group textarea,
      .form-group select {
        width: 100%;
        padding: 10px;
        border: 1px solid var(--md-sys-color-outline);
        border-radius: 4px;
        font-size: 14px;
        background-color: var(--md-sys-color-surface);
        color: var(--md-sys-color-on-surface);
      }

      .form-group textarea {
        min-height: 80px;
        resize: vertical;
      }

      button {
        padding: 10px 20px;
        background-color: var(--md-sys-color-primary);
        color: var(--md-sys-color-on-primary);
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
      }

      button:hover {
        opacity: 0.9;
      }

      button.secondary {
        background-color: var(--md-sys-color-secondary);
        color: var(--md-sys-color-on-secondary);
      }

      .steps-container {
        margin-top: 20px;
      }

      .steps-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 15px;
      }

      .steps-header h2 {
        margin: 0;
        font-size: 18px;
      }

      .step-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .step-item {
        background-color: var(--md-sys-color-surface-variant);
        border: 1px solid var(--md-sys-color-outline);
        border-radius: 8px;
        padding: 15px;
        cursor: pointer;
        transition:
          transform 0.2s,
          box-shadow 0.2s;
      }

      .step-item:hover {
        transform: translateY(-2px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .step-item.selected {
        border-color: var(--md-sys-color-primary);
        border-width: 2px;
      }

      .step-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
      }

      .step-title {
        font-weight: bold;
        font-size: 16px;
      }

      .step-actions {
        display: flex;
        gap: 5px;
      }

      .step-actions button {
        padding: 5px 10px;
        font-size: 12px;
      }

      .step-type {
        display: inline-block;
        padding: 4px 8px;
        background-color: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
        border-radius: 4px;
        font-size: 12px;
        margin-bottom: 8px;
      }

      .step-config {
        font-size: 12px;
        color: var(--md-sys-color-on-surface-variant);
        margin-top: 8px;
      }

      .empty-state {
        text-align: center;
        padding: 40px;
        color: var(--md-sys-color-on-surface-variant);
      }

      .add-step-button {
        width: 100%;
        padding: 15px;
        border: 2px dashed var(--md-sys-color-outline);
        background-color: transparent;
        color: var(--md-sys-color-on-surface);
        cursor: pointer;
        border-radius: 8px;
        margin-top: 10px;
      }

      .add-step-button:hover {
        border-color: var(--md-sys-color-primary);
        background-color: var(--md-sys-color-surface-variant);
      }

      .loading {
        text-align: center;
        padding: 40px;
      }

      .error {
        padding: 15px;
        background-color: #ffebee;
        color: #c62828;
        border-radius: 4px;
        margin-bottom: 20px;
      }
    `
  ]

  @property({ type: String }) workflowId?: string
  @state() workflow: any = {
    name: '',
    description: '',
    projectId: null,
    triggerType: 'manual',
    triggerConfig: '',
    steps: [],
    autoStart: false
  }
  @state() selectedStepIndex: number = -1
  @state() loading: boolean = false
  @state() error: string = ''

  async firstUpdated() {
    if (this.workflowId) {
      await this.loadWorkflow()
    }
  }

  async loadWorkflow() {
    this.loading = true
    this.error = ''

    try {
      const response = await client.query({
        query: gql`
          query GetWorkflow($workflowId: String!) {
            labelingWorkflow(workflowId: $workflowId) {
              id
              name
              description
              projectId
              triggerType
              triggerConfig
              steps {
                name
                type
                order
                config
                continueOnError
              }
              status
              autoStart
            }
          }
        `,
        variables: { workflowId: this.workflowId }
      })

      this.workflow = response.data.labelingWorkflow
    } catch (error) {
      console.error('Failed to load workflow:', error)
      this.error = 'Failed to load workflow. Please try again.'
    } finally {
      this.loading = false
    }
  }

  async saveWorkflow() {
    this.loading = true
    this.error = ''

    try {
      const mutation = this.workflowId
        ? gql`
            mutation UpdateWorkflow($workflowId: String!, $input: UpdateWorkflowRequest!) {
              updateLabelingWorkflow(workflowId: $workflowId, input: $input) {
                id
                name
              }
            }
          `
        : gql`
            mutation CreateWorkflow($input: CreateWorkflowRequest!) {
              createLabelingWorkflow(input: $input) {
                id
                name
              }
            }
          `

      const variables: any = {
        input: {
          name: this.workflow.name,
          description: this.workflow.description,
          projectId: parseInt(this.workflow.projectId),
          triggerType: this.workflow.triggerType,
          triggerConfig: this.workflow.triggerConfig,
          steps: this.workflow.steps.map((step: any, index: number) => ({
            name: step.name,
            type: step.type,
            order: index + 1,
            config: step.config,
            continueOnError: step.continueOnError || false
          })),
          autoStart: this.workflow.autoStart
        }
      }

      if (this.workflowId) {
        variables.workflowId = this.workflowId
      }

      await client.mutate({ mutation, variables })

      alert('Workflow saved successfully!')
      history.back()
    } catch (error) {
      console.error('Failed to save workflow:', error)
      this.error = 'Failed to save workflow. Please check your inputs and try again.'
    } finally {
      this.loading = false
    }
  }

  addStep() {
    const newStep = {
      name: `Step ${this.workflow.steps.length + 1}`,
      type: 'import_data',
      config: '{}',
      continueOnError: false
    }

    this.workflow = {
      ...this.workflow,
      steps: [...this.workflow.steps, newStep]
    }

    this.selectedStepIndex = this.workflow.steps.length - 1
  }

  removeStep(index: number) {
    if (confirm('Are you sure you want to remove this step?')) {
      const steps = [...this.workflow.steps]
      steps.splice(index, 1)

      this.workflow = {
        ...this.workflow,
        steps
      }

      if (this.selectedStepIndex === index) {
        this.selectedStepIndex = -1
      } else if (this.selectedStepIndex > index) {
        this.selectedStepIndex--
      }
    }
  }

  moveStepUp(index: number) {
    if (index === 0) return

    const steps = [...this.workflow.steps]
    const temp = steps[index]
    steps[index] = steps[index - 1]
    steps[index - 1] = temp

    this.workflow = {
      ...this.workflow,
      steps
    }

    if (this.selectedStepIndex === index) {
      this.selectedStepIndex = index - 1
    } else if (this.selectedStepIndex === index - 1) {
      this.selectedStepIndex = index
    }
  }

  moveStepDown(index: number) {
    if (index === this.workflow.steps.length - 1) return

    const steps = [...this.workflow.steps]
    const temp = steps[index]
    steps[index] = steps[index + 1]
    steps[index + 1] = temp

    this.workflow = {
      ...this.workflow,
      steps
    }

    if (this.selectedStepIndex === index) {
      this.selectedStepIndex = index + 1
    } else if (this.selectedStepIndex === index + 1) {
      this.selectedStepIndex = index
    }
  }

  selectStep(index: number) {
    this.selectedStepIndex = index
  }

  updateStep(index: number, field: string, value: any) {
    const steps = [...this.workflow.steps]
    steps[index] = {
      ...steps[index],
      [field]: value
    }

    this.workflow = {
      ...this.workflow,
      steps
    }
  }

  updateWorkflow(field: string, value: any) {
    this.workflow = {
      ...this.workflow,
      [field]: value
    }
  }

  renderStepEditor() {
    if (this.selectedStepIndex === -1 || !this.workflow.steps[this.selectedStepIndex]) {
      return html` <div class="empty-state">Select a step to edit its configuration</div> `
    }

    const step = this.workflow.steps[this.selectedStepIndex]

    return html`
      <div class="form-group">
        <label>Step Name</label>
        <input
          type="text"
          .value=${step.name}
          @input=${(e: any) => this.updateStep(this.selectedStepIndex, 'name', e.target.value)}
        />
      </div>

      <div class="form-group">
        <label>Step Type</label>
        <select
          .value=${step.type}
          @change=${(e: any) => this.updateStep(this.selectedStepIndex, 'type', e.target.value)}
        >
          <option value="import_data">Import Data</option>
          <option value="generate_predictions">Generate Predictions</option>
          <option value="wait_for_annotations">Wait for Annotations</option>
          <option value="sync_annotations">Sync Annotations</option>
          <option value="validate_quality">Validate Quality</option>
          <option value="export_results">Export Results</option>
          <option value="notification">Notification</option>
        </select>
      </div>

      <div class="form-group">
        <label>Configuration (JSON)</label>
        <textarea
          .value=${step.config}
          @input=${(e: any) => this.updateStep(this.selectedStepIndex, 'config', e.target.value)}
          placeholder='{"key": "value"}'
        ></textarea>
      </div>

      <div class="form-group">
        <label>
          <input
            type="checkbox"
            .checked=${step.continueOnError}
            @change=${(e: any) => this.updateStep(this.selectedStepIndex, 'continueOnError', e.target.checked)}
          />
          Continue on Error
        </label>
      </div>
    `
  }

  render() {
    if (this.loading) {
      return html`<div class="loading">Loading...</div>`
    }

    return html`
      <div class="header">
        <h1>${this.workflowId ? 'Edit Workflow' : 'Create Workflow'}</h1>
        <div class="header-actions">
          <button class="secondary" @click=${() => history.back()}>Cancel</button>
          <button @click=${this.saveWorkflow}>Save Workflow</button>
        </div>
      </div>

      ${this.error ? html`<div class="error">${this.error}</div>` : ''}

      <div class="content">
        <div class="sidebar">
          <div class="form-group">
            <label>Workflow Name *</label>
            <input
              type="text"
              .value=${this.workflow.name}
              @input=${(e: any) => this.updateWorkflow('name', e.target.value)}
              placeholder="Enter workflow name"
              required
            />
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea
              .value=${this.workflow.description || ''}
              @input=${(e: any) => this.updateWorkflow('description', e.target.value)}
              placeholder="Enter workflow description"
            ></textarea>
          </div>

          <div class="form-group">
            <label>Project ID *</label>
            <input
              type="number"
              .value=${this.workflow.projectId || ''}
              @input=${(e: any) => this.updateWorkflow('projectId', e.target.value)}
              placeholder="Label Studio project ID"
              required
            />
          </div>

          <div class="form-group">
            <label>Trigger Type</label>
            <select
              .value=${this.workflow.triggerType}
              @change=${(e: any) => this.updateWorkflow('triggerType', e.target.value)}
            >
              <option value="manual">Manual</option>
              <option value="scheduled">Scheduled</option>
              <option value="event">Event-based</option>
            </select>
          </div>

          <div class="form-group">
            <label>
              <input
                type="checkbox"
                .checked=${this.workflow.autoStart}
                @change=${(e: any) => this.updateWorkflow('autoStart', e.target.checked)}
              />
              Auto Start
            </label>
          </div>

          <div class="steps-container">
            <div class="steps-header">
              <h2>Workflow Steps</h2>
            </div>

            <div class="step-list">
              ${this.workflow.steps.length === 0
                ? html`<div class="empty-state">No steps added yet</div>`
                : this.workflow.steps.map(
                    (step: any, index: number) => html`
                      <div
                        class="step-item ${this.selectedStepIndex === index ? 'selected' : ''}"
                        @click=${() => this.selectStep(index)}
                      >
                        <div class="step-header">
                          <div class="step-title">${index + 1}. ${step.name}</div>
                          <div class="step-actions">
                            ${index > 0
                              ? html`<button
                                  @click=${(e: Event) => {
                                    e.stopPropagation()
                                    this.moveStepUp(index)
                                  }}
                                >
                                  ↑
                                </button>`
                              : ''}
                            ${index < this.workflow.steps.length - 1
                              ? html`<button
                                  @click=${(e: Event) => {
                                    e.stopPropagation()
                                    this.moveStepDown(index)
                                  }}
                                >
                                  ↓
                                </button>`
                              : ''}
                            <button
                              class="secondary"
                              @click=${(e: Event) => {
                                e.stopPropagation()
                                this.removeStep(index)
                              }}
                            >
                              ×
                            </button>
                          </div>
                        </div>
                        <div class="step-type">${step.type}</div>
                        ${step.continueOnError ? html`<div class="step-config">Continue on error: Yes</div>` : ''}
                      </div>
                    `
                  )}
            </div>

            <button class="add-step-button" @click=${this.addStep}>+ Add Step</button>
          </div>
        </div>

        <div class="main-area">
          <h2>Step Configuration</h2>
          ${this.renderStepEditor()}
        </div>
      </div>
    `
  }
}
